import type {
  CronofyAccountState,
  CronofyElementsContext,
  CronofyElementToken,
} from '@efdigital/ll-addon-coaching/types/cronofy-elements-context';
import { AdminSessionsRequestError } from '@efdigital/ll-admin-v2/data/builders/coaching/admin-sessions';
import type AdminCronofyService from '@efdigital/ll-admin-v2/services/admin-cronofy';
import type { CronofyElementType } from '@efdigital/ll-cdk-coaching-cronofy-api-types';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cell, resource, use } from 'ember-resources';

interface AdminCronofyElementTokenProviderSignature {
  Args: {
    coachId: string;
  };
  Blocks: {
    default: [cronofyContext: CronofyElementsContext];
  };
}

const DEFAULT_STATUS: CronofyAccountState = {
  hasCronofyAccount: false,
  dataCenter: 'uk',
  loading: true,
};

/** Same element type the reschedule picker asks for on mount. */
const STATUS_ELEMENT_TYPE: CronofyElementType = 'date-time-picker';

interface ProviderState {
  account: CronofyAccountState;
  loadFailed: boolean;
}

const DEFAULT_STATE: ProviderState = {
  account: DEFAULT_STATUS,
  loadFailed: false,
};

function isMissingCronofyAccount(error: unknown): boolean {
  return error instanceof AdminSessionsRequestError && error.status === 404;
}

/**
 * Admin equivalent of `CoacheeElementTokenProvider`/`CoachService` from
 * `@efdigital/ll-addon-coaching` — implements `CronofyElementsContext` so
 * `<CronofyDateTimePicker>` can mount against any coach's real calendar.
 *
 * There's no admin calendar-status endpoint (unlike coachee), so status and
 * the first element token come from the same call — the admin element-token
 * response already includes `dataCenter`/`accountSub` alongside the token.
 * That token is cached and handed to the first `fetchElementToken` for the
 * same element type so the picker doesn't immediately repeat the request.
 */
export class AdminCronofyElementTokenProvider
  extends Component<AdminCronofyElementTokenProviderSignature>
  implements CronofyElementsContext
{
  @service declare adminCronofy: AdminCronofyService;

  /**
   * Token from the status request. Consumed once by the first matching
   * `fetchElementToken` call; later requests go to the API.
   */
  private unusedElementToken: CronofyElementToken | null = null;

  @use private state = resource(() => {
    const coachId = this.args.coachId;
    const state = cell<ProviderState>(DEFAULT_STATE);
    this.unusedElementToken = null;

    this.adminCronofy
      .getElementToken(coachId, STATUS_ELEMENT_TYPE, window.location.origin)
      .then((data) => {
        if (this.args.coachId !== coachId) {
          return;
        }
        this.unusedElementToken = {
          elementToken: data.elementToken,
          accountSub: data.accountSub,
        };
        state.set({
          account: {
            hasCronofyAccount: true,
            dataCenter: data.dataCenter,
            loading: false,
            accountSub: data.accountSub,
          },
          loadFailed: false,
        });
      })
      .catch((error: unknown) => {
        if (this.args.coachId !== coachId) {
          return;
        }
        console.error('Failed to load coach Cronofy status', error);
        this.unusedElementToken = null;
        state.set({
          account: {
            hasCronofyAccount: false,
            dataCenter: 'uk',
            loading: false,
          },
          loadFailed: !isMissingCronofyAccount(error),
        });
      });

    return () => state.current;
  });

  async fetchElementToken(
    elementType: CronofyElementType
  ): Promise<CronofyElementToken> {
    if (this.accountStatus.loading || !this.accountStatus.hasCronofyAccount) {
      throw new Error('Coach has no Cronofy account');
    }

    const cached = this.unusedElementToken;
    if (cached && elementType === STATUS_ELEMENT_TYPE) {
      this.unusedElementToken = null;
      return cached;
    }

    const data = await this.adminCronofy.getElementToken(
      this.args.coachId,
      elementType,
      window.location.origin
    );
    return { elementToken: data.elementToken, accountSub: data.accountSub };
  }

  get accountStatus(): CronofyAccountState {
    return this.state.account;
  }

  <template>
    {{#if this.accountStatus.loading}}
      <p class="text-body-secondary">Loading coach availability…</p>
    {{else if this.accountStatus.hasCronofyAccount}}
      {{yield this}}
    {{else if this.state.loadFailed}}
      <p class="text-danger mb-0">
        Couldn't load this coach's availability. Close and reopen this dialog to
        try again — this can be a temporary issue rather than a disconnected
        calendar.
      </p>
    {{else}}
      <p class="text-danger mb-0">
        This coach's calendar isn't connected — reschedule isn't available until
        they reconnect Cronofy.
      </p>
    {{/if}}
  </template>
}
